import { newTierId } from './defaults';
import {
  AGGREGATES,
  type Aggregate,
  type DesignConfig,
  type TagKey,
  type TierConfig,
} from './types';

/**
 * URL-hash serialisation for a whole design.
 *
 * The point of the permalink is that an engineer can paste a link into a ticket or
 * a chat thread and the person reading it sees exactly the same numbers. That means
 * the format has to survive being copied, truncated by a chat client, or written by
 * an older version of the app — so it is versioned, compact, and every decode path
 * is total: a malformed hash yields an error, never a throw and never a half-applied
 * config.
 *
 * Encoding is short-keyed JSON in base64url. It compresses the default design to
 * roughly 300 characters, which comfortably fits a URL everywhere it matters, and
 * unlike a binary packing it stays debuggable — you can base64-decode a link by hand
 * and read what it says.
 */

export const PERMALINK_VERSION = 1;
const PREFIX = `v${PERMALINK_VERSION}.`;

export type DecodeResult = { ok: true; config: DesignConfig } | { ok: false; error: string };

/* -------------------------------------------------------------------------- */
/* base64url                                                                   */
/* -------------------------------------------------------------------------- */

function toBase64Url(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(encoded: string): string {
  if (!/^[A-Za-z0-9\-_]+$/.test(encoded)) throw new Error('not base64url');
  const padded = encoded.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/* -------------------------------------------------------------------------- */
/* encode                                                                      */
/* -------------------------------------------------------------------------- */

/** Aggregates become their index in {@link AGGREGATES}, e.g. `['mean','max']` → `"03"`. */
function packAggregates(aggregates: Aggregate[]): string {
  return aggregates
    .map((a) => AGGREGATES.indexOf(a))
    .filter((i) => i >= 0)
    .join('');
}

function unpackAggregates(packed: unknown): Aggregate[] {
  if (typeof packed !== 'string') return ['mean'];
  const out: Aggregate[] = [];
  for (const ch of packed) {
    const agg = AGGREGATES[Number(ch)];
    if (agg && !out.includes(agg)) out.push(agg);
  }
  return out.length > 0 ? out : ['mean'];
}

/** Serialise a config to the hash payload (without the leading `#`). */
export function encodeConfig(config: DesignConfig): string {
  const compact = {
    s: {
      b: config.source.bucket,
      o: config.source.org,
      m: config.source.measurement,
      f: config.source.fields,
      t: config.source.tagKeys.map((t) => [t.key, t.cardinality, t.retained ? 1 : 0]),
      i: config.source.interval,
      r: config.source.retention,
    },
    t: config.tiers.map((t) => [
      t.bucket,
      t.window,
      packAggregates(t.aggregates),
      t.retention,
      t.createEmpty ? 1 : 0,
    ]),
    c: [
      config.schedule.every,
      config.schedule.offset,
      config.schedule.cron,
      config.schedule.lateTolerance,
    ],
    a: [config.assumptions.bytesPerPoint, config.assumptions.horizonDays],
  };
  return PREFIX + toBase64Url(JSON.stringify(compact));
}

/* -------------------------------------------------------------------------- */
/* decode                                                                      */
/* -------------------------------------------------------------------------- */

function str(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

function num(value: unknown, fallback: number, min: number, max: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function bool(value: unknown): boolean {
  return value === 1 || value === true;
}

function strArray(value: unknown, fallback: string[]): string[] {
  if (!Array.isArray(value)) return fallback;
  const out = value.filter((v): v is string => typeof v === 'string');
  return out.length > 0 ? out : fallback;
}

function tagKeys(value: unknown): TagKey[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((entry): entry is unknown[] => Array.isArray(entry))
    .map((entry) => ({
      key: str(entry[0], ''),
      cardinality: Math.round(num(entry[1], 1, 1, 100_000_000)),
      retained: bool(entry[2]),
    }))
    .filter((t) => t.key !== '');
}

function tiers(value: unknown): TierConfig[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((entry): entry is unknown[] => Array.isArray(entry))
    .slice(0, 12)
    .map((entry) => ({
      id: newTierId(),
      bucket: str(entry[0], 'rollup'),
      window: str(entry[1], '1h'),
      aggregates: unpackAggregates(entry[2]),
      retention: str(entry[3], '90d'),
      createEmpty: bool(entry[4]),
    }));
}

/**
 * Parse a hash payload back into a config.
 *
 * Accepts the payload with or without a leading `#`. Unknown or missing values fall
 * back to something sensible rather than failing the whole decode; only a payload
 * that is not a readable, correctly-versioned document is rejected outright.
 */
export function decodeConfig(hash: string): DecodeResult {
  const payload = hash.replace(/^#/, '').trim();
  if (payload === '') return { ok: false, error: 'empty permalink' };

  const dot = payload.indexOf('.');
  if (dot < 0 || !payload.startsWith('v')) {
    return { ok: false, error: 'missing version prefix — expected something like "v1.<data>"' };
  }
  const version = Number(payload.slice(1, dot));
  if (!Number.isInteger(version) || version < 1) {
    return { ok: false, error: 'unreadable version number' };
  }
  if (version > PERMALINK_VERSION) {
    return {
      ok: false,
      error: `this link was created by a newer version of the designer (v${version})`,
    };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(fromBase64Url(payload.slice(dot + 1)));
  } catch {
    return { ok: false, error: 'the link is truncated or corrupted' };
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { ok: false, error: 'the link does not contain a design' };
  }

  const doc = parsed as Record<string, unknown>;
  const s = (typeof doc.s === 'object' && doc.s !== null ? doc.s : {}) as Record<string, unknown>;
  const schedule = Array.isArray(doc.c) ? doc.c : [];
  const assumptions = Array.isArray(doc.a) ? doc.a : [];
  const decodedTiers = tiers(doc.t);
  if (decodedTiers.length === 0) {
    return { ok: false, error: 'the link contains no downsampling tiers' };
  }

  return {
    ok: true,
    config: {
      source: {
        bucket: str(s.b, 'telemetry_raw'),
        org: str(s.o, 'my-org'),
        measurement: str(s.m, 'sensor_reading'),
        fields: strArray(s.f, ['value']),
        tagKeys: tagKeys(s.t),
        interval: str(s.i, '10s'),
        retention: str(s.r, '7d'),
      },
      tiers: decodedTiers,
      schedule: {
        every: str(schedule[0], ''),
        offset: str(schedule[1], '0s'),
        cron: str(schedule[2], ''),
        lateTolerance: str(schedule[3], '0s'),
      },
      assumptions: {
        bytesPerPoint: num(assumptions[0], 16, 0.1, 1024),
        horizonDays: Math.round(num(assumptions[1], 730, 1, 36_500)),
      },
    },
  };
}

/** Build a full shareable URL for `config` against the current location. */
export function permalinkFor(config: DesignConfig, base: string): string {
  const url = new URL(base);
  url.hash = encodeConfig(config);
  return url.toString();
}
